import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { isValidPhoneFormat } from "../../utils/phone";
import { toast } from "react-toastify";
import {
  Building2,
  User,
  MapPin,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertTriangle,
  Eye,
  EyeOff,
  Globe,
  Phone,
  Mail,
  Lock,
  FileText,
  Info,
  Truck,
  Package,
} from "lucide-react";
import { useAppSelector, useAppDispatch } from "../../hooks/useStore";
import type { CompanyType } from "../../types";
import { createCompany } from "../../store/slices/superAdminCompanySlice";
import { superAdminCompanyAPI } from "../../services/api";
import { FormField, Input, Select } from "../../components/UI/FormField";
import Button from "../../components/UI/Button";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Country {
  _id: string;
  name: string;
  code: string;
}

interface FormValues {
  // Step 1 — Company Info
  name: string;
  subdomain: string;
  companyType: CompanyType;
  phone?: string;
  registrationOrLicenseNumber?: string;
  // Step 2 — Address
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalcode?: string;
    country?: string;
  };
  // Step 3 — Admin User
  createAdmin: boolean;
  adminUser?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
  };
}

// ─── Validation Schemas (per step) ───────────────────────────────────────────

// ─── Company type options ────────────────────────────────────────────────────

const COMPANY_TYPE_OPTIONS: {
  value: CompanyType;
  label: string;
  description: string;
  icon: typeof Truck;
}[] = [
  {
    value: "forwarder",
    label: "Forwarder",
    description:
      "Sells transport — plans shipments, assigns drivers, invoices customers.",
    icon: Truck,
  },
  {
    value: "shipper",
    label: "Shipper",
    description:
      "Buys transport — books cargo with forwarders and tracks its own shipments.",
    icon: Package,
  },
];

const step1Schema = yup.object({
  name: yup
    .string()
    .required("Company name is required")
    .max(200, "Max 200 characters"),
  subdomain: yup
    .string()
    .required("Subdomain is required")
    .max(50, "Max 50 characters")
    .matches(
      /^[a-z0-9-]+$/,
      "Only lowercase letters, numbers, and hyphens allowed",
    )
    .notOneOf(
      [
        "admin",
        "api",
        "api-v2",
        "www",
        "app",
        "dashboard",
        "mail",
        "smtp",
        "ftp",
      ],
      "This subdomain is reserved",
    ),
  companyType: yup
    .string()
    .required("Company type is required")
    .oneOf(["forwarder", "shipper"], "Select forwarder or shipper"),
  phone: yup
    .string()
    .optional()
    .test(
      "phone-format",
      "Enter a valid phone number — digits, optionally with +, spaces, brackets or dashes",
      (value) => isValidPhoneFormat(value),
    ),
  registrationOrLicenseNumber: yup.string().optional(),
});

const step2Schema = yup.object({
  address: yup
    .object({
      street: yup.string().optional(),
      city: yup.string().optional(),
      state: yup.string().optional(),
      postalcode: yup.string().optional(),
      country: yup.string().optional(),
    })
    .optional(),
});

const step3Schema = yup.object({
  createAdmin: yup.boolean().required(),
  adminUser: yup.object().when("createAdmin", {
    is: true,
    then: (schema) =>
      schema.shape({
        firstName: yup.string().required("First name is required"),
        lastName: yup.string().required("Last name is required"),
        email: yup
          .string()
          .required("Email is required")
          .email("Invalid email address"),
        password: yup
          .string()
          .optional()
          .test(
            "password-strength",
            "Password must be at least 8 characters",
            (val) => !val || val.length >= 8,
          ),
        confirmPassword: yup
          .string()
          .optional()
          .test("passwords-match", "Passwords do not match", function (val) {
            const { password } = this.parent;
            if (!password) return true; // both empty = fine
            return val === password;
          }),
      }),
    otherwise: (schema) => schema.optional(),
  }),
});

const fullSchema = step1Schema
  .concat(step2Schema as any)
  .concat(step3Schema as any);

// ─── Step Config ─────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: "Company Info", icon: Building2 },
  { id: 2, label: "Address", icon: MapPin },
  { id: 3, label: "Admin User", icon: User },
  { id: 4, label: "Review", icon: Check },
];

// ─── Component ───────────────────────────────────────────────────────────────

const CreateCompanyPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((s) => s.superAdminCompanies);

  const [step, setStep] = useState(1);
  const [countries, setCountries] = useState<Country[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(
    null,
  );
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);

  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(fullSchema as any),
    defaultValues: {
      createAdmin: true,
      companyType: "forwarder",
    },
    mode: "onChange",
  });

  const watchedSubdomain = useWatch({ control, name: "subdomain" });
  const watchedCompanyType = useWatch({ control, name: "companyType" });
  const watchedCreateAdmin = useWatch({ control, name: "createAdmin" });
  const watchedPassword = useWatch({ control, name: "adminUser.password" });

  // Load countries
  useEffect(() => {
    superAdminCompanyAPI
      .getCountries()
      .then((res) => setCountries(res.data.data.countries || []))
      .catch(() => toast.error("Failed to load countries"));
  }, []);

  // Subdomain availability check (debounced)
  useEffect(() => {
    if (!watchedSubdomain || watchedSubdomain.length < 2) {
      setSubdomainAvailable(null);
      return;
    }

    const reserved = [
      "admin",
      "api",
      "api-v2",
      "www",
      "app",
      "dashboard",
      "mail",
      "smtp",
      "ftp",
    ];
    if (reserved.includes(watchedSubdomain.toLowerCase())) {
      setSubdomainAvailable(false);
      return;
    }

    if (!/^[a-z0-9-]+$/.test(watchedSubdomain)) {
      setSubdomainAvailable(null);
      return;
    }

    const timeout = setTimeout(async () => {
      setCheckingSubdomain(true);
      try {
        // The backend returns 400 if the subdomain exists
        // We use the getAll endpoint with a search to check loosely
        // A dedicated /check-subdomain endpoint would be ideal, but we
        // handle it gracefully: if createCompany returns "Subdomain already exists"
        // we show the toast — this is just a UX hint
        const res = await superAdminCompanyAPI.getAllCompanies({
          search: watchedSubdomain,
          limit: 5,
        });
        const exact = res.data.data.companies.some(
          (c: any) => c.subdomain === watchedSubdomain.toLowerCase(),
        );
        setSubdomainAvailable(!exact);
      } catch {
        setSubdomainAvailable(null);
      } finally {
        setCheckingSubdomain(false);
      }
    }, 600);

    return () => clearTimeout(timeout);
  }, [watchedSubdomain]);

  // ── Step navigation ────────────────────────────────────────────────────────

  const stepFields: Record<number, (keyof FormValues)[]> = {
    1: [
      "name",
      "subdomain",
      "companyType",
      "phone",
      "registrationOrLicenseNumber",
    ],
    2: ["address"],
    3: ["createAdmin", "adminUser"],
  };

  const goNext = async () => {
    const valid = await trigger(stepFields[step] as any);
    if (!valid) return;
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  /**
   * Strip empty strings, nulls, and undefined from an address object.
   * Prevents Mongoose "Cast to ObjectId failed" when country select is "".
   */
  const sanitizeAddress = (
    address?: FormValues["address"],
  ): FormValues["address"] | undefined => {
    if (!address) return undefined;
    const cleaned = Object.fromEntries(
      Object.entries(address as Record<string, unknown>).filter(
        ([, v]) => v !== "" && v !== null && v !== undefined,
      ),
    ) as FormValues["address"];
    return Object.keys(cleaned as object).length > 0 ? cleaned : undefined;
  };

  const onSubmit = async (data: FormValues) => {
    const payload: any = {
      name: data.name,
      subdomain: data.subdomain.toLowerCase(),
      companyType: data.companyType,
      phone: data.phone || undefined,
      registrationOrLicenseNumber:
        data.registrationOrLicenseNumber || undefined,
      address: sanitizeAddress(data.address),
    };

    if (data.createAdmin && data.adminUser?.email) {
      payload.adminUser = {
        firstName: data.adminUser.firstName,
        lastName: data.adminUser.lastName,
        email: data.adminUser.email,
        ...(data.adminUser.password
          ? { password: data.adminUser.password }
          : {}),
      };
    }

    try {
      const result = await dispatch(createCompany(payload)).unwrap();

      // Show warnings non-intrusively if DNS / email had issues
      if (result.warnings?.length) {
        result.warnings.forEach((w: string) =>
          toast.warn(w, { autoClose: 8000 }),
        );
      }

      // Invite flow feedback
      if (result.adminUser?.inviteToken) {
        toast.success(
          `Company created! Invite email sent to ${result.adminUser.email}.`,
          { autoClose: 6000 },
        );
      } else {
        toast.success("Company created successfully!");
      }

      navigate(`/companies/${result.company._id}`);
    } catch (err: any) {
      // Handle specific known errors
      if (typeof err === "string" && err.toLowerCase().includes("subdomain")) {
        setStep(1);
        toast.error("Subdomain already exists. Please choose another.");
      } else if (
        typeof err === "string" &&
        err.toLowerCase().includes("admin email")
      ) {
        setStep(3);
        toast.error("Admin email already exists for this company.");
      } else {
        toast.error(err || "Failed to create company. Please try again.");
      }
    }
  };

  // ── Derived UI helpers ─────────────────────────────────────────────────────

  const values = getValues();
  const hasPassword = !!watchedPassword?.trim();

  const subdomainPreview = watchedSubdomain
    ? `${watchedSubdomain.toLowerCase()}.livecargo.ee`
    : "yoursubdomain.livecargo.ee";

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate("/companies")}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Create Company
            </h1>
            <p className="text-sm text-gray-500">
              Set up a new tenant on the platform
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Stepper */}
        <div className="mb-8">
          <div className="flex items-center justify-between relative">
            {/* Progress line */}
            <div className="absolute left-0 right-0 top-5 h-0.5 bg-gray-200 z-0" />
            <div
              className="absolute left-0 top-5 h-0.5 bg-primary z-0 transition-all duration-500"
              style={{ width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }}
            />

            {STEPS.map((s) => {
              const Icon = s.icon;
              const isCompleted = step > s.id;
              const isActive = step === s.id;

              return (
                <div
                  key={s.id}
                  className="flex flex-col items-center z-10 gap-2"
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                      isCompleted
                        ? "bg-primary border-primary text-white"
                        : isActive
                          ? "bg-white border-primary text-primary"
                          : "bg-white border-gray-300 text-gray-400"
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span
                    className={`text-xs font-medium whitespace-nowrap ${
                      isActive
                        ? "text-primary"
                        : isCompleted
                          ? "text-gray-700"
                          : "text-gray-400"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* ── Step 1: Company Info ─────────────────────────────────────── */}
            {step === 1 && (
              <div>
                <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    <h2 className="text-base font-semibold text-gray-900">
                      Company Information
                    </h2>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Basic details about the new company
                  </p>
                </div>

                <div className="p-3 space-y-1">
                  {/* Name */}
                  <FormField
                    layout="inline"
                    size="sm"
                    label="Company Name"
                    required
                    error={errors.name?.message}
                  >
                    <Input
                      size="sm"
                      {...register("name")}
                      placeholder="e.g. Acme Logistics OÜ"
                      error={!!errors.name}
                    />
                  </FormField>

                  {/* Company type */}
                  <FormField
                    layout="inline"
                    size="sm"
                    label="Company Type"
                    required
                    error={errors.companyType?.message}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {COMPANY_TYPE_OPTIONS.map((opt) => {
                        const OptIcon = opt.icon;
                        const isSelected = watchedCompanyType === opt.value;

                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              setValue("companyType", opt.value, {
                                shouldValidate: true,
                              })
                            }
                            className={`text-left p-3 rounded-lg border-2 transition-all ${
                              isSelected
                                ? "border-primary bg-primary/5"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <OptIcon
                                className={`w-4 h-4 ${isSelected ? "text-primary" : "text-gray-400"}`}
                              />
                              <span
                                className={`text-sm font-medium ${isSelected ? "text-primary" : "text-gray-900"}`}
                              >
                                {opt.label}
                              </span>
                              {isSelected && (
                                <Check className="w-4 h-4 text-primary ml-auto" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {opt.description}
                            </p>
                          </button>
                        );
                      })}
                    </div>

                    <p className="text-xs text-gray-400 mt-1 flex items-start gap-1">
                      <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      Determines which cockpit the tenant gets. Can be changed
                      later from the company detail page.
                    </p>
                  </FormField>

                  {/* Subdomain */}
                  <FormField
                    layout="inline"
                    size="sm"
                    label="Subdomain"
                    required
                    error={errors.subdomain?.message}
                  >
                    <div className="relative">
                      <Input
                        size="sm"
                        {...register("subdomain")}
                        placeholder="e.g. acme"
                        error={!!errors.subdomain}
                        className="pr-10"
                        onChange={(e) => {
                          // Force lowercase as user types
                          e.target.value = e.target.value.toLowerCase();
                          register("subdomain").onChange(e);
                        }}
                      />
                      {/* Availability indicator */}
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {checkingSubdomain && (
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        )}
                        {!checkingSubdomain && subdomainAvailable === true && (
                          <Check className="w-4 h-4 text-green-500" />
                        )}
                        {!checkingSubdomain && subdomainAvailable === false && (
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                    </div>

                    {/* Live preview */}
                    <div className="mt-2 flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="text-xs text-gray-500">
                        Preview:{" "}
                        <span
                          className={`font-mono font-medium ${watchedSubdomain ? "text-primary" : "text-gray-400"}`}
                        >
                          {subdomainPreview}
                        </span>
                      </span>
                      {subdomainAvailable === false && !errors.subdomain && (
                        <span className="text-xs text-red-500 font-medium">
                          · Taken
                        </span>
                      )}
                      {subdomainAvailable === true && !errors.subdomain && (
                        <span className="text-xs text-green-600 font-medium">
                          · Available
                        </span>
                      )}
                    </div>

                    {/* Subdomain rules hint */}
                    <p className="text-xs text-gray-400 mt-1 flex items-start gap-1">
                      <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      Lowercase letters, numbers, and hyphens only. Cannot be
                      changed after creation.
                    </p>
                  </FormField>

                  {/* Phone */}
                  <FormField
                    layout="inline"
                    size="sm"
                    label="Phone Number"
                    error={errors.phone?.message}
                  >
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        size="sm"
                        {...register("phone")}
                        placeholder="+372 5123 4567"
                        className="pl-9"
                        error={!!errors.phone}
                      />
                    </div>
                  </FormField>

                  {/* Reg number */}
                  <FormField
                    layout="inline"
                    size="sm"
                    label="Registration / License Number"
                    error={errors.registrationOrLicenseNumber?.message}
                  >
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        size="sm"
                        {...register("registrationOrLicenseNumber")}
                        placeholder="e.g. REG-2024-001234"
                        className="pl-9"
                        error={!!errors.registrationOrLicenseNumber}
                      />
                    </div>
                  </FormField>
                </div>
              </div>
            )}

            {/* ── Step 2: Address ──────────────────────────────────────────── */}
            {step === 2 && (
              <div>
                <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    <h2 className="text-base font-semibold text-gray-900">
                      Company Address
                    </h2>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    All address fields are optional
                  </p>
                </div>

                <div className="p-3 space-y-1">
                  <FormField
                    layout="inline"
                    size="sm"
                    label="Street"
                    error={errors.address?.street?.message}
                  >
                    <Input
                      size="sm"
                      {...register("address.street")}
                      placeholder="e.g. Pärnu maantee 15"
                      error={!!errors.address?.street}
                    />
                  </FormField>

                  <FormField
                    layout="inline"
                    size="sm"
                    label="City"
                    error={errors.address?.city?.message}
                  >
                    <Input
                      size="sm"
                      {...register("address.city")}
                      placeholder="e.g. Tallinn"
                      error={!!errors.address?.city}
                    />
                  </FormField>

                  <FormField
                    layout="inline"
                    size="sm"
                    label="State / County"
                    error={errors.address?.state?.message}
                  >
                    <Input
                      size="sm"
                      {...register("address.state")}
                      placeholder="e.g. Harju"
                      error={!!errors.address?.state}
                    />
                  </FormField>

                  <FormField
                    layout="inline"
                    size="sm"
                    label="Postal Code"
                    error={errors.address?.postalcode?.message}
                  >
                    <Input
                      size="sm"
                      {...register("address.postalcode")}
                      placeholder="e.g. 10141"
                      error={!!errors.address?.postalcode}
                    />
                  </FormField>

                  <FormField
                    layout="inline"
                    size="sm"
                    label="Country"
                    error={errors.address?.country?.message}
                  >
                    <Select
                      size="sm"
                      {...register("address.country")}
                      error={!!errors.address?.country}
                      placeholder="Select a country"
                    >
                      {countries.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.name} ({c.code})
                        </option>
                      ))}
                    </Select>
                  </FormField>
                </div>
              </div>
            )}

            {/* ── Step 3: Admin User ───────────────────────────────────────── */}
            {step === 3 && (
              <div>
                <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    <h2 className="text-base font-semibold text-gray-900">
                      Admin User
                    </h2>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Optionally create the first admin for this company
                  </p>
                </div>

                <div className="p-3 space-y-1">
                  {/* Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Create admin user now
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        You can also add users later from the company detail
                        page
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setValue("createAdmin", !watchedCreateAdmin)
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                        watchedCreateAdmin ? "bg-primary" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          watchedCreateAdmin ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {watchedCreateAdmin && (
                    <div className="space-y-1 animate-in fade-in slide-in-from-top-2 duration-200">
                      <FormField
                        layout="inline"
                        size="sm"
                        label="First Name"
                        required
                        error={errors.adminUser?.firstName?.message}
                      >
                        <Input
                          size="sm"
                          {...register("adminUser.firstName")}
                          placeholder="John"
                          error={!!errors.adminUser?.firstName}
                        />
                      </FormField>

                      <FormField
                        layout="inline"
                        size="sm"
                        label="Last Name"
                        required
                        error={errors.adminUser?.lastName?.message}
                      >
                        <Input
                          size="sm"
                          {...register("adminUser.lastName")}
                          placeholder="Doe"
                          error={!!errors.adminUser?.lastName}
                        />
                      </FormField>

                      <FormField
                        layout="inline"
                        size="sm"
                        label="Email Address"
                        required
                        error={errors.adminUser?.email?.message}
                      >
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            size="sm"
                            {...register("adminUser.email")}
                            type="email"
                            placeholder="admin@company.com"
                            className="pl-9"
                            error={!!errors.adminUser?.email}
                          />
                        </div>
                      </FormField>

                      {/* Password section */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-700">
                              Password Setup
                            </span>
                          </div>
                        </div>

                        <div className="p-4 space-y-4">
                          {/* Mode info */}
                          <div
                            className={`flex gap-3 p-3 rounded-lg text-sm ${hasPassword ? "bg-blue-50 text-blue-800 border border-blue-100" : "bg-amber-50 text-amber-800 border border-amber-100"}`}
                          >
                            <Info className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>
                              {hasPassword
                                ? "Admin will be created as active and can log in immediately with this password."
                                : "No password set — an invite email will be sent automatically so the admin can set their own password. Account will be pending until accepted."}
                            </span>
                          </div>

                          <FormField
                            layout="inline"
                            size="sm"
                            label="Password"
                            error={errors.adminUser?.password?.message}
                          >
                            <div className="relative">
                              <Input
                                size="sm"
                                {...register("adminUser.password")}
                                type={showPassword ? "text" : "password"}
                                placeholder="Leave empty to send invite email"
                                className="pr-10"
                                error={!!errors.adminUser?.password}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword((s) => !s)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showPassword ? (
                                  <EyeOff className="w-4 h-4" />
                                ) : (
                                  <Eye className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </FormField>

                          {hasPassword && (
                            <FormField
                              layout="inline"
                              size="sm"
                              label="Confirm Password"
                              error={errors.adminUser?.confirmPassword?.message}
                            >
                              <div className="relative">
                                <Input
                                  size="sm"
                                  {...register("adminUser.confirmPassword")}
                                  type={showConfirm ? "text" : "password"}
                                  placeholder="Repeat password"
                                  className="pr-10"
                                  error={!!errors.adminUser?.confirmPassword}
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowConfirm((s) => !s)}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  {showConfirm ? (
                                    <EyeOff className="w-4 h-4" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </FormField>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Step 4: Review ───────────────────────────────────────────── */}
            {step === 4 && (
              <div>
                <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-primary" />
                    <h2 className="text-base font-semibold text-gray-900">
                      Review & Confirm
                    </h2>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Double-check everything before creating the company
                  </p>
                </div>

                <div className="p-3 space-y-1.5">
                  {/* Company section */}
                  <ReviewSection
                    title="Company Information"
                    onEdit={() => setStep(1)}
                  >
                    <ReviewRow label="Name" value={values.name} />
                    <ReviewRow
                      label="Company Type"
                      value={
                        COMPANY_TYPE_OPTIONS.find(
                          (o) => o.value === values.companyType,
                        )?.label || values.companyType
                      }
                    />
                    <ReviewRow
                      label="Subdomain"
                      value={`${values.subdomain}.livecargo.ee`}
                      mono
                    />
                    {values.phone && (
                      <ReviewRow label="Phone" value={values.phone} />
                    )}
                    {values.registrationOrLicenseNumber && (
                      <ReviewRow
                        label="Reg. Number"
                        value={values.registrationOrLicenseNumber}
                      />
                    )}
                  </ReviewSection>

                  {/* Address section */}
                  {values.address &&
                  Object.values(values.address).some(Boolean) ? (
                    <ReviewSection title="Address" onEdit={() => setStep(2)}>
                      {values.address.street && (
                        <ReviewRow
                          label="Street"
                          value={values.address.street}
                        />
                      )}
                      {values.address.city && (
                        <ReviewRow label="City" value={values.address.city} />
                      )}
                      {values.address.state && (
                        <ReviewRow label="State" value={values.address.state} />
                      )}
                      {values.address.postalcode && (
                        <ReviewRow
                          label="Postal Code"
                          value={values.address.postalcode}
                        />
                      )}
                      {values.address.country && (
                        <ReviewRow
                          label="Country"
                          value={
                            countries.find(
                              (c) => c._id === values.address?.country,
                            )?.name || values.address.country
                          }
                        />
                      )}
                    </ReviewSection>
                  ) : (
                    <ReviewSection title="Address" onEdit={() => setStep(2)}>
                      <p className="text-sm text-gray-400 italic">
                        No address provided
                      </p>
                    </ReviewSection>
                  )}

                  {/* Admin User section */}
                  <ReviewSection title="Admin User" onEdit={() => setStep(3)}>
                    {values.createAdmin && values.adminUser?.email ? (
                      <>
                        <ReviewRow
                          label="Name"
                          value={`${values.adminUser.firstName} ${values.adminUser.lastName}`}
                        />
                        <ReviewRow
                          label="Email"
                          value={values.adminUser.email}
                        />
                        <ReviewRow
                          label="Setup"
                          value={
                            values.adminUser.password
                              ? "Password set — account active immediately"
                              : "No password — invite email will be sent"
                          }
                          highlight={!values.adminUser.password}
                        />
                      </>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        No admin user — will be added later
                      </p>
                    )}
                  </ReviewSection>

                  {/* DNS note */}
                  <div className="flex gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800">
                    <Globe className="w-4 h-4 shrink-0 mt-0.5 text-blue-500" />
                    <span>
                      A DNS record will be automatically provisioned for{" "}
                      <span className="font-mono font-medium">
                        {values.subdomain}.livecargo.ee
                      </span>
                      . If DNS provisioning fails, it can be retried from the
                      company detail page.
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ── Footer: Navigation buttons ───────────────────────────────── */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <div>
                {step > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={goBack}
                    icon={<ChevronLeft className="w-4 h-4" />}
                    disabled={loading}
                  >
                    Back
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate("/companies")}
                  disabled={loading}
                >
                  Cancel
                </Button>

                {step < 4 ? (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={goNext}
                    icon={<ChevronRight className="w-4 h-4" />}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={loading}
                    icon={!loading ? <Check className="w-4 h-4" /> : undefined}
                  >
                    {loading ? "Creating..." : "Create Company"}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Step hint */}
          <p className="text-center text-xs text-gray-400 mt-4">
            Step {step} of {STEPS.length}
          </p>
        </form>
      </div>
    </div>
  );
};

// ─── Small helper components ──────────────────────────────────────────────────

const ReviewSection: React.FC<{
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}> = ({ title, onEdit, children }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
      <span className="text-sm font-semibold text-gray-700">{title}</span>
      <button
        type="button"
        onClick={onEdit}
        className="text-xs text-primary hover:text-primary-700 font-medium transition-colors"
      >
        Edit
      </button>
    </div>
    <div className="px-4 py-3 space-y-2">{children}</div>
  </div>
);

const ReviewRow: React.FC<{
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
}> = ({ label, value, mono, highlight }) => (
  <div className="flex items-start gap-4">
    <span className="text-sm text-gray-500 w-32 shrink-0">{label}</span>
    <span
      className={`text-sm break-all ${
        mono
          ? "font-mono text-primary"
          : highlight
            ? "text-amber-700 font-medium"
            : "text-gray-900 font-medium"
      }`}
    >
      {value}
    </span>
  </div>
);

export default CreateCompanyPage;
